import React, { useMemo, useState, useSyncExternalStore } from "react";
import { useDraggable } from "@dnd-kit/core";
import DraggablePanel from "./DraggablePanel";
import Checkbox from "../ui/Checkbox";
import InfoTooltip from "../ui/InfoTooltip";
import PhysicsAuthoringLockNotice from "../ui/PhysicsAuthoringLockNotice";
import Tooltip from "../ui/Tooltip";
import { useHFields, useSceneActions } from "../../stores/sceneStore";
import { DEFAULT_SCENE_FLOOR_SIZE } from "../../viewer/sceneDefaults";
import {
  getStandardFloorHField,
  MAX_HFIELD_HEIGHT_SCALE,
  STANDARD_FLOOR_HFIELD_NAME,
} from "../../utils/terrainFloor";
import {
  getTerrainVisualizationOptions,
  setTerrainVisualizationOptions,
  subscribeTerrainVisualizationOptions,
} from "../../utils/terrainVisualizationOptions";
import { buildTerrainData, TERRAIN_PRESETS, terrainSample } from "../../utils/terrainPresetData";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";

const TERRAIN_PANEL_HELP_TEXT =
  'Add a "false floor" for your robot to move on. Click or drag a preset onto the viewport.';
const PHYSICS_LOCK_TERRAIN_TEXT = "Stop physics to change terrain.";

/** @param {number} m */
function formatTerrainHeightMeters(m) {
  if (!Number.isFinite(m)) return "—";
  const abs = Math.abs(m);
  const decimals = abs >= 1 ? 2 : abs >= 0.1 ? 2 : 3;
  return `${m.toFixed(decimals)} m`;
}

/** Match MainPanel `SceneSquareToggleButton` / main menu panel tiles. */
const MAIN_MENU_HOVER_SURFACE_CLASS = "main-menu-hover-surface";
const MAIN_MENU_DRAGGABLE_PANEL_TOGGLE_CLASS = "main-menu-draggable-panel-toggle";
const TERRAIN_PRESET_BUTTON_CLASS = `${MAIN_MENU_HOVER_SURFACE_CLASS} ${MAIN_MENU_DRAGGABLE_PANEL_TOGGLE_CLASS} relative flex min-h-20 min-w-0 w-full flex-col items-center justify-center gap-1.5 rounded-md border bg-transparent px-2 py-2 text-center text-gray-200 transition-colors duration-[400ms] ease-out focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500`;
/** Same persistent gray as main menu open toggles (index.css `main-menu-panel-toggle-open`). */
const MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS = "main-menu-panel-toggle-open";
const TERRAIN_PRESET_BUTTON_BORDER_STYLE = Object.freeze({ borderColor: "var(--panel-border)" });
const TERRAIN_PRESET_LABEL_CLASS =
  "max-w-full whitespace-normal break-words text-center text-xs font-medium leading-tight text-gray-200";

/** Matches `terrainSlopeHeatColor`: hue = (1 - normalizedSlope) * 0.66 at S=0.95, L=0.5 (Three.js HSL). */
const SLOPE_HEATMAP_LEGEND_GRADIENT =
  "linear-gradient(to right, hsl(237.6deg 95% 50%), hsl(178.2deg 95% 50%), hsl(118.8deg 95% 50%), hsl(59.4deg 95% 50%), hsl(0deg 95% 50%))";

const THUMB_STROKE = "#a1a1aa";

/** Cross-section preview at fixed v; matches `terrainSample` (slope preset: shallow line, not full-height normalization). */
function TerrainPresetThumbnail({ presetKey }) {
  const className = "mx-auto block h-6 w-[3.45rem] shrink-0";
  if (presetKey === "slope") {
    return (
      <svg viewBox="0 0 64 28" className={className} aria-hidden>
        <line
          x1="6"
          y1="17"
          x2="58"
          y2="12"
          fill="none"
          stroke={THUMB_STROKE}
          strokeWidth="1.25"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  const segments = presetKey === "rough" ? 52 : 40;
  const v = 0.5;
  const ys = [];
  for (let i = 0; i <= segments; i += 1) {
    const u = i / segments;
    ys.push(terrainSample(presetKey, u, v));
  }
  let min = Infinity;
  let max = -Infinity;
  ys.forEach((y) => {
    if (y < min) min = y;
    if (y > max) max = y;
  });
  const range = max - min;
  const normalized = range < 1e-9 ? ys.map(() => 0.5) : ys.map((y) => (y - min) / range);

  const left = 6;
  const right = 58;
  const top = 6;
  const bottom = 22;
  const parts = normalized.map((nv, i) => {
    const x = left + (i / segments) * (right - left);
    const y = bottom - nv * (bottom - top);
    return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  });

  return (
    <svg viewBox="0 0 64 28" className={className} aria-hidden>
      <path
        d={parts.join(" ")}
        fill="none"
        stroke={THUMB_STROKE}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TerrainPresetButton = React.memo(function TerrainPresetButton({
  preset,
  isActive,
  onPresetClick,
  disabled,
}) {
  const wasDraggingRef = React.useRef(false);
  const draggableId = `terrain-preset:${preset.key}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: draggableId,
    disabled,
    data: {
      dragSource: "terrainPanel",
      itemType: "terrainPreset",
      presetKey: preset.key,
      previewName: `${preset.label} terrain`,
    },
  });

  React.useEffect(() => {
    if (isDragging) wasDraggingRef.current = true;
  }, [isDragging]);

  const handleClick = React.useCallback(() => {
    if (disabled) return;
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    onPresetClick(preset);
  }, [disabled, onPresetClick, preset]);

  return (
    <div
      ref={setNodeRef}
      {...(disabled ? {} : listeners)}
      {...(disabled ? {} : attributes)}
      className={`draggable-library-item min-w-0 ${isDragging ? "opacity-[0.42]" : ""}`}
    >
      <Tooltip
        content={disabled ? PHYSICS_LOCK_TERRAIN_TEXT : preset.description}
        fullWidth
        side="top"
      >
        <button
          type="button"
          disabled={disabled}
          aria-pressed={isActive}
          aria-label={
            isActive
              ? `${preset.label} (active). Click again to remove terrain.`
              : `${preset.label}. Drag into the viewport to add terrain, or click. ${preset.description}`
          }
          onClick={handleClick}
          style={TERRAIN_PRESET_BUTTON_BORDER_STYLE}
          className={`${TERRAIN_PRESET_BUTTON_CLASS} ${isActive ? MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS : ""} ${disabled ? "cursor-not-allowed opacity-45" : ""}`}
        >
          <TerrainPresetThumbnail presetKey={preset.key} />
          <span className={TERRAIN_PRESET_LABEL_CLASS}>{preset.label}</span>
        </button>
      </Tooltip>
    </div>
  );
});

const TerrainPresetGrid = React.memo(function TerrainPresetGrid({
  activeTerrainPresetKey,
  onPresetClick,
  disabled,
}) {
  return (
    <>
      {TERRAIN_PRESETS.map((preset) => (
        <TerrainPresetButton
          key={preset.key}
          preset={preset}
          isActive={activeTerrainPresetKey === preset.key}
          onPresetClick={onPresetClick}
          disabled={disabled}
        />
      ))}
    </>
  );
});

const TerrainPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const authoringLocked = usePhysicsAuthoringLock();
  const hfields = useHFields();
  const sceneActions = useSceneActions();
  const terrainVisualizationOptions = useSyncExternalStore(
    subscribeTerrainVisualizationOptions,
    getTerrainVisualizationOptions,
    getTerrainVisualizationOptions
  );
  const heatmapColorByHeight = terrainVisualizationOptions.heatmapColorMode === "height";

  const [terrainParams, setTerrainParams] = useState({});

  const activeTerrainPresetKey = useMemo(() => {
    const floorHf = getStandardFloorHField(hfields);
    if (!floorHf) return null;
    const match = TERRAIN_PRESETS.find((p) => p.nrow === floorHf.nrow && p.ncol === floorHf.ncol);
    return match?.key ?? null;
  }, [hfields]);

  const activePreset = useMemo(
    () => TERRAIN_PRESETS.find((p) => p.key === activeTerrainPresetKey) ?? null,
    [activeTerrainPresetKey]
  );

  const buildAndApplyTerrain = (preset, params) => {
    if (authoringLocked) return;
    const halfExtent = DEFAULT_SCENE_FLOOR_SIZE / 2;
    const name = STANDARD_FLOOR_HFIELD_NAME;
    const prev = getStandardFloorHField(hfields);
    const prevScale = Number(prev?.heightScale);
    const heightScale = Math.min(
      MAX_HFIELD_HEIGHT_SCALE,
      Number.isFinite(prevScale) && prevScale >= 0 ? prevScale : 1
    );
    const hfield = {
      name,
      size: [halfExtent, halfExtent, preset.elevation, preset.base],
      nrow: preset.nrow,
      ncol: preset.ncol,
      data: buildTerrainData(preset.key, preset.nrow, preset.ncol, params),
      heightScale,
    };
    sceneActions.setHFields({ ...(hfields || {}), [name]: hfield });
  };

  const clearTerrain = () => {
    if (authoringLocked) return;
    if (!getStandardFloorHField(hfields)) return;
    const next = { ...(hfields || {}) };
    delete next[STANDARD_FLOOR_HFIELD_NAME];
    sceneActions.setHFields(next);
  };

  const handlePresetClick = (preset) => {
    if (activeTerrainPresetKey === preset.key) {
      clearTerrain();
      return;
    }
    const defaultParams = preset.defaultParams ?? {};
    setTerrainParams(defaultParams);
    buildAndApplyTerrain(preset, defaultParams);
  };

  const handleParamChange = (newParams) => {
    if (authoringLocked || !activePreset) return;
    setTerrainParams(newParams);
    buildAndApplyTerrain(activePreset, newParams);
  };

  const getParam = (key) =>
    terrainParams[key] !== undefined
      ? terrainParams[key]
      : (activePreset?.defaultParams?.[key] ?? 0);

  const floorHf = getStandardFloorHField(hfields);
  const baseElevation =
    floorHf && Array.isArray(floorHf.size) && Number.isFinite(Number(floorHf.size[2]))
      ? Math.max(0, Number(floorHf.size[2]))
      : 0;
  const rawHeightScale =
    floorHf && Number.isFinite(Number(floorHf.heightScale)) && Number(floorHf.heightScale) >= 0
      ? Number(floorHf.heightScale)
      : 1;
  const maxTotalHeight = baseElevation * MAX_HFIELD_HEIGHT_SCALE;
  const totalHeight = baseElevation * Math.min(MAX_HFIELD_HEIGHT_SCALE, rawHeightScale);
  const heightSliderStep =
    maxTotalHeight <= 0
      ? 0.001
      : maxTotalHeight < 0.2
        ? 0.002
        : maxTotalHeight < 0.6
          ? 0.005
          : 0.01;

  const setTotalHeightMeters = (meters) => {
    if (authoringLocked) return;
    const hf = getStandardFloorHField(hfields);
    if (!hf) return;
    const base =
      Array.isArray(hf.size) && Number.isFinite(Number(hf.size[2]))
        ? Math.max(0, Number(hf.size[2]))
        : 0;
    if (base < 1e-9) return;
    const maxH = base * MAX_HFIELD_HEIGHT_SCALE;
    const v = Math.max(0, Math.min(maxH, Number(meters)));
    const nextScale = Number.isFinite(v) ? v / base : 1;
    sceneActions.setHFields({
      ...(hfields || {}),
      [STANDARD_FLOOR_HFIELD_NAME]: { ...hf, heightScale: nextScale },
    });
  };

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Terrain"
      titleIcon={titleIcon}
      titleSuffix={
        <span data-no-panel-drag="true" className="inline-flex items-center">
          <InfoTooltip text={TERRAIN_PANEL_HELP_TEXT} widthClass="w-80" />
        </span>
      }
      initialSize={{ width: 380, height: 520 }}
      minWidth={320}
      minHeight={360}
      libraryPanelRoot
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto p-3 text-sm text-gray-100">
        <PhysicsAuthoringLockNotice actionText={PHYSICS_LOCK_TERRAIN_TEXT} />
        <section className="shrink-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-gray-200">Presets</div>
            <Tooltip content={authoringLocked ? PHYSICS_LOCK_TERRAIN_TEXT : null}>
              <button
                type="button"
                data-no-panel-drag="true"
                disabled={authoringLocked || !getStandardFloorHField(hfields)}
                onClick={clearTerrain}
                className="shrink-0 rounded border border-gray-600 bg-transparent px-2 py-1 text-xs text-gray-200 transition-colors hover:bg-[rgba(80,80,80,0.35)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Clear terrain
              </button>
            </Tooltip>
          </div>
          <div className="grid w-full grid-cols-2 gap-3">
            <TerrainPresetGrid
              activeTerrainPresetKey={activeTerrainPresetKey}
              onPresetClick={handlePresetClick}
              disabled={authoringLocked}
            />
          </div>
        </section>

        {activePreset && (
          <section className="shrink-0 space-y-3" data-no-panel-drag="true">
            <div className="text-sm font-semibold text-gray-200">Shape</div>
            {"frequency" in (activePreset.defaultParams ?? {}) && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Frequency</span>
                  <span className="min-w-[3rem] text-right text-xs text-gray-300 tabular-nums select-none">
                    {getParam("frequency").toFixed(2)}×
                  </span>
                </div>
                <Tooltip content={authoringLocked ? PHYSICS_LOCK_TERRAIN_TEXT : null} fullWidth>
                  <input
                    type="range"
                    min={0.25}
                    max={4.0}
                    step={0.05}
                    value={getParam("frequency")}
                    disabled={authoringLocked}
                    className={`min-w-0 w-full h-2 slider ${authoringLocked ? "cursor-not-allowed opacity-40" : ""}`}
                    aria-label="Terrain frequency"
                    onChange={(e) =>
                      handleParamChange({
                        ...terrainParams,
                        frequency: parseFloat(e.target.value),
                      })
                    }
                  />
                </Tooltip>
              </div>
            )}
            {"roughness" in (activePreset.defaultParams ?? {}) && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Roughness</span>
                  <span className="min-w-[3rem] text-right text-xs text-gray-300 tabular-nums select-none">
                    {getParam("roughness").toFixed(2)}
                  </span>
                </div>
                <Tooltip content={authoringLocked ? PHYSICS_LOCK_TERRAIN_TEXT : null} fullWidth>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={getParam("roughness")}
                    disabled={authoringLocked}
                    className={`min-w-0 w-full h-2 slider ${authoringLocked ? "cursor-not-allowed opacity-40" : ""}`}
                    aria-label="Terrain roughness"
                    onChange={(e) =>
                      handleParamChange({ ...terrainParams, roughness: parseFloat(e.target.value) })
                    }
                  />
                </Tooltip>
              </div>
            )}
            <div className="relative z-10 space-y-2">
              <Tooltip
                content="Vertical amplitude of the heightfield: the full base-to-peak elevation range used in the scene, physics, and exported XML."
                side="top"
              >
                <div className="text-xs text-gray-400 cursor-default">Amplitude (total height)</div>
              </Tooltip>
              <div className="flex items-center gap-2">
                <Tooltip content={authoringLocked ? PHYSICS_LOCK_TERRAIN_TEXT : null} fullWidth>
                  <input
                    type="range"
                    min={0}
                    max={maxTotalHeight || 0}
                    step={heightSliderStep}
                    value={Number.isFinite(totalHeight) ? totalHeight : 0}
                    disabled={authoringLocked || !floorHf || baseElevation < 1e-9}
                    className={`min-w-0 flex-1 h-2 slider ${authoringLocked || !floorHf || baseElevation < 1e-9 ? "cursor-not-allowed opacity-40" : ""}`}
                    aria-label="Terrain amplitude total height in meters"
                    onChange={(e) => setTotalHeightMeters(parseFloat(e.target.value))}
                  />
                </Tooltip>
                <span className="min-w-[3.75rem] shrink-0 text-right text-xs text-gray-300 tabular-nums select-none">
                  {formatTerrainHeightMeters(totalHeight)}
                </span>
              </div>
              {baseElevation < 1e-9 ? (
                <p className="text-xs panel-text-helper">No elevation range on this preset.</p>
              ) : null}
            </div>
          </section>
        )}

        <section className="shrink-0 space-y-2" data-no-panel-drag="true">
          <Tooltip
            content="When terrain shape or total height changes, move every world-root object so its lowest point stays on the terrain surface (same bottom correction as dropping an item from the Library). Orientation is unchanged."
            side="top"
          >
            <div className="text-sm font-semibold text-gray-200 cursor-default">Scene objects</div>
          </Tooltip>
          <Checkbox
            label="Move objects when terrain changes"
            className="text-xs text-gray-200 w-auto px-0 py-0"
            checked={!!terrainVisualizationOptions.liftBodiesWithTerrainEdits}
            onChange={(event) =>
              setTerrainVisualizationOptions({ liftBodiesWithTerrainEdits: event.target.checked })
            }
          />
        </section>

        <section
          className={`shrink-0 space-y-2 ${!floorHf ? "opacity-55" : ""}`}
          aria-disabled={!floorHf}
        >
          <div className="text-sm font-semibold text-gray-200">Visualization</div>
          {!floorHf ? (
            <p className="text-xs panel-text-helper">
              Add a terrain preset to use heatmap, contours, or grid.
            </p>
          ) : null}
          <div className="flex flex-col gap-1">
            <div
              className="flex min-w-0 flex-nowrap items-center gap-1.5"
              data-no-panel-drag="true"
            >
              <Checkbox
                label="Heatmap"
                className="max-w-fit shrink-0 px-0 py-0 text-xs text-gray-200 !w-fit [&>span:last-child]:flex-none"
                disabled={!floorHf}
                checked={!!floorHf && terrainVisualizationOptions.showHeatmap}
                onChange={(event) =>
                  setTerrainVisualizationOptions({ showHeatmap: event.target.checked })
                }
              />
              {terrainVisualizationOptions.showHeatmap ? (
                <>
                  <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide panel-text-secondary">
                    Color by
                  </span>
                  <div
                    className="inline-flex shrink-0 rounded-md border border-gray-700 bg-gray-900/30 p-0.5"
                    role="group"
                    aria-label="Heatmap: slope or height"
                  >
                    <button
                      type="button"
                      disabled={!floorHf}
                      className={`rounded px-2 py-1 text-xs transition-colors select-none ${
                        !heatmapColorByHeight
                          ? "bg-gray-700 text-gray-100"
                          : "text-gray-300 hover:text-gray-100"
                      }`}
                      aria-pressed={!heatmapColorByHeight}
                      onClick={() => setTerrainVisualizationOptions({ heatmapColorMode: "slope" })}
                    >
                      Slope
                    </button>
                    <button
                      type="button"
                      disabled={!floorHf}
                      className={`rounded px-2 py-1 text-xs transition-colors select-none ${
                        heatmapColorByHeight
                          ? "bg-gray-700 text-gray-100"
                          : "text-gray-300 hover:text-gray-100"
                      }`}
                      aria-pressed={heatmapColorByHeight}
                      onClick={() => setTerrainVisualizationOptions({ heatmapColorMode: "height" })}
                    >
                      Height
                    </button>
                  </div>
                </>
              ) : null}
            </div>
            <Checkbox
              label="Contours"
              className="text-xs text-gray-200 w-auto px-0 py-0"
              disabled={!floorHf}
              checked={!!floorHf && terrainVisualizationOptions.showContours}
              onChange={(event) =>
                setTerrainVisualizationOptions({ showContours: event.target.checked })
              }
            />
            <Checkbox
              label="Grid"
              className="text-xs text-gray-200 w-auto px-0 py-0"
              disabled={!floorHf}
              checked={!!floorHf && terrainVisualizationOptions.showSurfaceGrid}
              onChange={(event) =>
                setTerrainVisualizationOptions({ showSurfaceGrid: event.target.checked })
              }
            />
          </div>
          {floorHf && terrainVisualizationOptions.showHeatmap ? (
            <div
              className="mt-2 space-y-1.5 rounded-md border border-gray-700 bg-[rgba(30,30,30,0.5)] px-2.5 py-2"
              data-no-panel-drag="true"
            >
              <div className="text-sm font-semibold text-gray-200">
                {heatmapColorByHeight ? "Height heatmap scale" : "Slope heatmap scale"}
              </div>
              <div
                className="h-3 w-full rounded-full border border-gray-500 shadow-inner"
                style={{ background: SLOPE_HEATMAP_LEGEND_GRADIENT }}
                role="img"
                aria-label={
                  heatmapColorByHeight
                    ? "Height colors from blue for lower elevation to red for higher"
                    : "Slope colors from blue for gentle terrain to red for steep terrain"
                }
              />
              <div className="flex justify-between gap-2 text-xs font-medium leading-snug text-gray-300">
                <span>{heatmapColorByHeight ? "Lower" : "Gentle slope"}</span>
                <span>{heatmapColorByHeight ? "Higher" : "Steepest in scene"}</span>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(TerrainPanel);
